import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, Flex, VStack, HStack, Spacer } from '@chakra-ui/react';
import AssetManagerPage from 'components/common/AssetManagerPage';
import ProductView from 'components/common/ProductView';
import ProductManagerTile from 'components/common/ProductManagerTile';
import { LinkButton } from 'components/common/Buttons/LinkButton';
import TextWhite from 'components/common/TextWhite/TextWhite';
import { Pages } from 'constants/pages';
import Layout from 'components/common/Layout';
import Search from 'components/common/Search';
import Container from 'components/common/Container';
import { SimpleGrid } from 'components/UI/SimpleGrid';
import { checkIfError, matchString } from 'utils/helpers';
import { useFetchProducts, useDebounce, useToast } from 'hooks';
import AppRoutes from 'constants/routes';
import { IProductResource } from '../../../server/types';

const ProductManager = () => {
  const [products, setProducts] = useState<IProductResource[] | null>(null);
  const [showToast] = useToast();
  const [fetchProducts] = useFetchProducts();
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>();
  const [debouncedValue, setDebouncedValue] = useDebounce<string | undefined>(search);

  const handleClear = () => {
    setSearch('');
    setDebouncedValue('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const fetchData = async () => {
    const productsResult = await fetchProducts();
    if (checkIfError(productsResult)) {
      setProducts(null);
      showToast({ status: 'error', message: 'There was an error fetching your products' });
      return;
    }
    setProducts(productsResult.products);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const renderFields = () => {
    if (products == null) return null;

    return Object.entries(products)
      .filter(
        ([, product]) => !debouncedValue || !product.displayName || matchString(product.displayName, debouncedValue)
      )
      .map(([productKey, product]) => {
        const { name, displayName, thumbnail, thumbnailImage, id } = product;

        const url = `/productmanager/${id}`;

        return (
          <ProductManagerTile key={productKey}>
            <ProductView
              name={name as string}
              productKey={productKey}
              displayName={displayName as string}
              thumbnail={thumbnail ?? thumbnailImage}
              updateSelectedProduct={() => navigate(url)}
            />
          </ProductManagerTile>
        );
      });
  };

  return (
    <Layout testId="product-manager-page" pages={[Pages.ProductManager]}>
      <Helmet title="Product Manager" />
      <AssetManagerPage title="Product Manager">
        <Container>
          <HStack
            sx={{
              flex: 1,
              justifyContent: 'center',
            }}
            w="full"
          >
            <Flex bgColor="base2Colour.500" mb={6} w="full">
              <Box p={[2, 6]}>
                <Search
                  name="search"
                  value={search}
                  onChange={handleChange}
                  onClear={handleClear}
                  placeholder="Filter products by name..."
                  containerProps={{ maxW: 440 }}
                  pr={[14, 16]}
                />
              </Box>
              <Spacer />
              <Box p={[2, 6]}>
                <LinkButton
                  to={AppRoutes.NewProduct}
                  sx={{
                    pt: [15, 13],
                    pb: [15, 3.5],
                    textDecor: 'none',
                    hover: { textDecor: 'none' },
                    textAlign: 'center',
                  }}
                >
                  <Flex flexDir="row" gap={1}>
                    <TextWhite fontSize={[10, 13]}>ADD</TextWhite>
                    <TextWhite fontSize={[10, 13]} variant="second">
                      PRODUCT
                    </TextWhite>
                  </Flex>
                </LinkButton>
              </Box>
            </Flex>
          </HStack>

          <VStack flex={1} justifyContent="space-between" w="full">
            <Flex w="full" flexFlow="row wrap" justifyContent="center">
              <SimpleGrid w="full" columns={[1, 2, 3, 4, 5, 6]} columnGap={6} rowGap={6}>
                {renderFields()}
              </SimpleGrid>
            </Flex>
          </VStack>
        </Container>
      </AssetManagerPage>
    </Layout>
  );
};

export default ProductManager;
